import { useState, useEffect, useRef, type Dispatch, type SetStateAction } from "react"

const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000 // 1 day

interface Envelope<T> {
  value: T
  expiresAt: number
}

/**
 * Drop-in replacement for useState that persists the value in localStorage.
 * The stored value expires after `ttlMs` milliseconds (default: 1 day).
 * Expired or malformed entries are silently discarded and the defaultValue is used instead.
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T,
  ttlMs = DEFAULT_TTL_MS,
): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return defaultValue
      const envelope = JSON.parse(raw) as Envelope<T>
      if (Date.now() > envelope.expiresAt) {
        localStorage.removeItem(key)
        return defaultValue
      }
      return envelope.value
    } catch {
      return defaultValue
    }
  })

  // Keep a stable ref to ttlMs so the effect dep array doesn't change on every render
  const ttlRef = useRef(ttlMs)
  ttlRef.current = ttlMs

  useEffect(() => {
    try {
      const envelope: Envelope<T> = { value: state, expiresAt: Date.now() + ttlRef.current }
      localStorage.setItem(key, JSON.stringify(envelope))
    } catch {
      // localStorage unavailable (private mode, quota exceeded) — fail silently
    }
  }, [key, state])

  return [state, setState]
}
